/**
 * Feedback Modal Component
 * A simple modal for users to submit feedback.
 * Works for both authenticated and anonymous users.
 */

import React, { useState } from 'react';
import { useAuth } from '../../context/AuthenticationContext';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setErrorMessage('Please enter your feedback');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Include auth token if user is logged in
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: content.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit feedback');
      }

      setStatus('success');
      setContent('');
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
        setStatus('idle');
      }, 2000);
    } catch (error) {
      setErrorMessage(error.message);
      setStatus('error');
    }
  };

  const handleClose = () => {
    if (status !== 'submitting') {
      onClose();
      setContent('');
      setStatus('idle');
      setErrorMessage('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2>Send Feedback</h2>
          <button 
            className="feedback-modal-close" 
            onClick={handleClose}
            disabled={status === 'submitting'}
          >
            ×
          </button>
        </div>

        {status === 'success' ? (
          <div className="feedback-success">
            <div className="feedback-success-icon">✓</div>
            <p>Thank you for your feedback!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your thoughts, report a bug, or suggest an improvement..."
              className="feedback-textarea"
              disabled={status === 'submitting'}
              autoFocus
            />
            
            {status === 'error' && errorMessage && (
              <p className="feedback-error">{errorMessage}</p>
            )}

            <div className="feedback-form-footer">
              <button
                type="button"
                onClick={handleClose}
                className="feedback-btn-cancel"
                disabled={status === 'submitting'}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="feedback-btn-submit"
                disabled={status === 'submitting' || !content.trim()}
              >
                {status === 'submitting' ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
